import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import DockSidebar from './layout/DockSidebar';
import Topbar from './layout/Topbar';
import { routeConfig } from './layout/routeConfig';
import './layout/Layout.css';
import './Dashboard.css';

function Dashboard({ user, onLogout, onUpdateUser }) {
  const [settings, setSettings] = useState({});
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [showInactiveOverlay, setShowInactiveOverlay] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  
  // Sekmeleri localStorage'dan başlat
  const [openTabs, setOpenTabs] = useState(() => {
    const savedTabs = localStorage.getItem(`dentis_tabs_${user.id}`);
    if (savedTabs) {
      try {
        const parsed = JSON.parse(savedTabs);
        // routeConfig'den ikon ve başlıkları geri yükle
        return parsed.map(t => {
          const config = routeConfig[t.id];
          return config ? { ...t, title: config.title, icon: config.icon } : null;
        }).filter(Boolean);
      } catch (e) { console.error("Sekme yükleme hatası", e); }
    }
    return [];
  });

  const [activeTabId, setActiveTabId] = useState(() => {
    return localStorage.getItem(`dentis_active_tab_${user.id}`) || 'home';
  });
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [upgradeRequests, setUpgradeRequests] = useState([]);
  const [showRenewalAlert, setShowRenewalAlert] = useState(false);

  // Bileşenleri ID'ye göre haritala
  const componentMap = Object.values(routeConfig).reduce((acc, route) => {
    acc[route.id] = route.component;
    return acc;
  }, {});

  // Sekmeler değiştiğinde kaydet
  useEffect(() => {
    const tabsToSave = openTabs.map(t => ({ id: t.id, data: t.data }));
    localStorage.setItem(`dentis_tabs_${user.id}`, JSON.stringify(tabsToSave));
  }, [openTabs, user.id]);

  // Aktif sekme değiştiğinde kaydet
  useEffect(() => {
    localStorage.setItem(`dentis_active_tab_${user.id}`, activeTabId);
  }, [activeTabId, user.id]);

  useEffect(() => {
    // Impersonation kontrolü
    if (localStorage.getItem('original_user')) {
        setImpersonating(true);
    }
  }, []);

  useEffect(() => {
    axios.get('./backend/get_settings.php')
      .then(res => {
        // Lisans kontrolü: Eğer HTML dönerse (JSON değilse), sayfayı bu HTML ile değiştir
        if (typeof res.data === 'string' && (res.data.includes('<!DOCTYPE html>') || res.data.includes('Sistem Askıya Alındı'))) {
            document.documentElement.innerHTML = res.data;
            return;
        }
        if (res.data) setSettings(res.data);

        // Klinik Pasif Kontrolü (Süper Admin hariç)
        if (res.data.clinic_active == 0 && user.role !== 'super_admin') {
            setTimeout(() => {
                setShowInactiveOverlay(true);
            }, 2000);
        }

        // Abonelik Bitiş Uyarısı (7 gün kala)
        if (res.data.subscription_end && user.role !== 'super_admin') {
            const daysLeft = Math.ceil((new Date(res.data.subscription_end) - new Date()) / (1000 * 60 * 60 * 24));
            if (daysLeft <= 7) {
                setShowRenewalAlert(true);
            }
        }
        
        // Open the default tab
        if (openTabs.length === 0) {
            handleOpenTab('home');
        }
      })
      .catch(err => console.error(err));

      // Bildirimleri Çek
      const fetchNotifications = async () => {
        if (!user) return;

        // Süper Admin için özel talep kontrolü
        if (user.role === 'super_admin') {
            axios.get('./backend/get_upgrade_requests.php')
                .then(res => setUpgradeRequests(Array.isArray(res.data) ? res.data : []))
                .catch(err => console.error(err));
        }

        try {
            const requests = [
                axios.get(`./backend/get_appointments.php?role=${user.role}&user_id=${user.id}`),
                axios.get('./backend/get_announcements.php')
            ];
            // Sadece yetkili kişiler stok uyarısı görsün
            if (['admin', 'doctor'].includes(user.role)) {
                requests.push(axios.get('./backend/get_inventory.php'));
            }

            const responses = await Promise.all(requests);
            const appointments = Array.isArray(responses[0].data) ? responses[0].data : [];
            const announcements = Array.isArray(responses[1].data) ? responses[1].data : [];
            const inventory = responses[2] && Array.isArray(responses[2].data) ? responses[2].data : [];

            const newNotifications = [];
            const now = new Date();
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            
            // Temizlenmiş bildirimleri al
            const clearedNotifs = JSON.parse(localStorage.getItem(`cleared_notifs_${user.id}`) || '[]');
            const seenCriticals = JSON.parse(localStorage.getItem(`seen_critical_${user.id}`) || '[]');
            let hasNewCritical = false;

            // Yaklaşan Randevular
            appointments.forEach(app => {
                const appDate = new Date(app.appointment_date);
                if (appDate >= now && appDate <= tomorrow && app.status === 'scheduled' && !clearedNotifs.includes(`app-${app.id}`)) {
                    newNotifications.push({
                        id: `app-${app.id}`,
                        text: `Randevu: ${app.patient_name} - ${moment(app.appointment_date).format('HH:mm')}`,
                        time: moment(app.appointment_date).fromNow(),
                        tab: 'appointments',
                        date: appDate,
                        type: 'appointment'
                    });
                }
            });

            // Duyurular
            announcements.forEach(ann => {
                const notifId = `ann-${ann.id}`;
                
                // Kritik Duyuru Kontrolü (Otomatik Açılma)
                if (ann.type === 'danger' && !seenCriticals.includes(ann.id)) {
                    setSelectedAnnouncement(ann);
                    setShowAnnouncementModal(true);
                    seenCriticals.push(ann.id);
                    hasNewCritical = true;
                }

                if (!clearedNotifs.includes(notifId)) {
                    newNotifications.push({
                        id: notifId,
                        text: `📢 ${ann.title}`,
                        time: moment(ann.created_at).fromNow(),
                        tab: null,
                        date: new Date(ann.created_at),
                        type: ann.type, // info, warning, danger
                        data: ann // Tüm veriyi sakla
                    });
                }
            });

            // Kritik Stok
            inventory.forEach(item => {
                if (parseInt(item.quantity) <= parseInt(item.min_level) && !clearedNotifs.includes(`inv-${item.id}`)) {
                    newNotifications.push({
                        id: `inv-${item.id}`,
                        text: `Kritik Stok: ${item.item_name} (${item.quantity} ${item.unit})`,
                        time: 'Şimdi',
                        tab: 'inventory',
                        data: { filterLowStock: true },
                        date: new Date(), // En üstte
                        type: 'inventory'
                    });
                }
            });

            if (hasNewCritical) {
                localStorage.setItem(`seen_critical_${user.id}`, JSON.stringify(seenCriticals));
            }

            // Tarihe göre sırala
            newNotifications.sort((a, b) => a.date - b.date);
            setNotifications(newNotifications);
        } catch (error) {
            console.error("Bildirim hatası:", error);
        }
      };

      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000); // 1 dakikada bir güncelle
      return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const activeTab = openTabs.find(tab => tab.id === activeTabId);
    document.title = activeTab ? `${activeTab.title} - Dentis` : 'Dentis Panel';
  }, [activeTabId, openTabs]);

  const handleOpenTab = (tabId, data = null) => {
    if (!tabId) return;
    const existingTab = openTabs.find(tab => tab.id === tabId);
    if (!existingTab) {
      const route = routeConfig[tabId];
      if (route) {
        setOpenTabs([...openTabs, { id: route.id, title: route.title, icon: route.icon, data }]);
      }
    } else if (data) {
        // Sekme zaten açıksa ve yeni veri geldiyse güncelle
        setOpenTabs(openTabs.map(t => t.id === tabId ? { ...t, data } : t));
    }
    setActiveTabId(tabId);
  };

  const handleCloseTab = (tabId) => {
    const tabIndex = openTabs.findIndex(tab => tab.id === tabId);
    const newTabs = openTabs.filter(tab => tab.id !== tabId);
    setOpenTabs(newTabs);

    if (activeTabId === tabId) {
      if (newTabs.length > 0) {
        const newActiveIndex = Math.max(0, tabIndex - 1);
        setActiveTabId(newTabs[newActiveIndex].id);
      } else {
        // Fallback to home if all tabs are closed
        handleOpenTab('home');
      }
    }
  };

  const handleReorderTabs = (dragIndex, hoverIndex) => {
    const newTabs = [...openTabs];
    const [removed] = newTabs.splice(dragIndex, 1);
    newTabs.splice(hoverIndex, 0, removed);
    setOpenTabs(newTabs);
  };

  const handleCloseOthers = (tabId) => {
    const tabToKeep = openTabs.find(tab => tab.id === tabId);
    if (tabToKeep) {
      setOpenTabs([tabToKeep]);
      setActiveTabId(tabId);
    }
  };

  const handleCloseAll = () => {
    setOpenTabs([]);
    handleOpenTab('home');
  };

  const handleNotificationClick = (notification) => {
    if (notification.id.startsWith('ann-')) {
        setSelectedAnnouncement(notification.data);
        setShowAnnouncementModal(true);
    } else {
        handleOpenTab(notification.tab, notification.data);
    }
  };

  const handleExitImpersonation = () => {
    const originalUser = localStorage.getItem('original_user');
    if (originalUser) {
        const parsedUser = JSON.parse(originalUser);
        onUpdateUser(parsedUser);
        localStorage.removeItem('original_user');
        window.location.reload();
    }
  };

  const handleClearNotifications = () => {
      const currentIds = notifications.map(n => n.id);
      const cleared = JSON.parse(localStorage.getItem(`cleared_notifs_${user.id}`) || '[]');
      const newCleared = [...new Set([...cleared, ...currentIds])];
      localStorage.setItem(`cleared_notifs_${user.id}`, JSON.stringify(newCleared));
      setNotifications([]);
  };

  const handleRequestAction = (reqId, status) => {
      if (window.confirm(status === 'approved' ? 'Talebi onaylamak istiyor musunuz?' : 'Talebi reddetmek istiyor musunuz?')) {
          axios.post('./backend/handle_upgrade_request.php', { request_id: reqId, status })
              .then(res => {
                  // Listeyi güncelle
                  setUpgradeRequests(upgradeRequests.filter(r => r.id !== reqId));
              })
              .catch(err => console.error(err));
      }
  };

  const handleRenewalRequest = () => {
      if (window.confirm("Paket yenileme talebi göndermek istiyor musunuz?")) {
          // Mevcut plan ID'sini settings'den alıyoruz (settings state'inde plan_id var)
          axios.post('./backend/request_upgrade.php', { requested_plan_id: settings.plan_id })
              .then(res => toast.success(res.data.message))
              .catch(err => toast.error(err.response?.data?.message || "Talep gönderilemedi."));
      }
  };

  return (
    <div className={`dashboard-container-reborn ${isSidebarExpanded ? 'sidebar-expanded' : ''} ${isFullScreen ? 'fullscreen' : ''} role-${user.role}`}>
      
      {/* Impersonation Warning Bar */}
      {impersonating && (
          <div style={{position: 'fixed', top: 0, left: 0, width: '100%', background: '#f6c23e', color: '#333', padding: '5px 10px', textAlign: 'center', fontWeight: 'bold', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)'}}>
              <span>⚠️ Şu an <span style={{textDecoration: 'underline'}}>{settings.clinic_name || 'başka bir'}</span> kliniğini yönetiyorsunuz.</span>
              <button onClick={handleExitImpersonation} style={{background: '#333', color: 'white', border: 'none', padding: '5px 15px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem'}}>Yönetimden Çık</button>
          </div>
      )}

      {/* Pasif Klinik Overlay */}
      {showInactiveOverlay && (
          <div className="inactive-overlay">
              <div className="inactive-modal">
                  <div style={{fontSize: '3rem', marginBottom: '20px'}}>⚠️</div>
                  <h2>Hizmet Kesintisi</h2>
                  <p>Aboneliğinizin süresi dolmuş veya sisteminiz pasif duruma alınmıştır.</p>
                  <p>Lütfen yönetici ile iletişime geçiniz.</p>
                  <button onClick={onLogout} className="logout-btn" style={{marginTop: '20px', width: 'auto', padding: '10px 30px'}}>Çıkış Yap</button>
              </div>
          </div>
      )}

      {/* Duyuru Detay Modalı */}
      {showAnnouncementModal && selectedAnnouncement && (
          <div className="modal-overlay" style={{zIndex: 10000}}>
              <div className="modal" style={{maxWidth: '500px'}}>
                  <div className="modal-header" style={{
                      background: selectedAnnouncement.type === 'danger' ? '#e74a3b' : (selectedAnnouncement.type === 'warning' ? '#f6c23e' : '#4e73df')
                  }}>
                      <h3 style={{color: 'white'}}>{selectedAnnouncement.type === 'danger' ? '⚠️ KRİTİK DUYURU' : 'Duyuru Detayı'}</h3>
                      <button className="close-modal-btn" onClick={() => setShowAnnouncementModal(false)}>✕</button>
                  </div>
                  <div className="modal-body">
                      <h4 style={{marginTop: 0, color: '#333'}}>{selectedAnnouncement.title}</h4>
                      <div style={{color: '#555', lineHeight: '1.5'}} dangerouslySetInnerHTML={{__html: selectedAnnouncement.message}} />
                      <small style={{display: 'block', marginTop: '15px', color: '#999'}}>{moment(selectedAnnouncement.created_at).format('LLL')}</small>
                  </div>
                  <div className="modal-actions"><button className="save-btn" onClick={() => setShowAnnouncementModal(false)}>Kapat</button></div>
              </div>
          </div>
      )}

      {/* Süper Admin Talep Modalı (Basitçe Dashboard üstüne ekliyoruz veya ayrı modal yapılabilir) */}
      {user.role === 'super_admin' && upgradeRequests.length > 0 && (
          <div style={{position: 'fixed', bottom: '20px', right: '20px', background: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 5px 20px rgba(0,0,0,0.2)', zIndex: 9999, maxWidth: '400px', border: '1px solid #4e73df'}}>
              <h4 style={{marginTop: 0, color: '#4e73df'}}>📦 Paket Yükseltme Talepleri</h4>
              {upgradeRequests.map(req => {
                  const isRenewal = req.current_plan_id === req.requested_plan_id;
                  return (
                  <div key={req.id} style={{borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '10px'}}>
                      <p style={{margin: '5px 0'}}><strong>{req.clinic_name}</strong></p>
                      <p style={{margin: '5px 0', fontSize: '0.9rem'}}>{isRenewal ? 'Paket Yenileme Talebi' : `${req.current_plan_name} ➔ ${req.requested_plan_name}`}</p>
                      {isRenewal && <span style={{fontSize: '0.8rem', background: '#e3f2fd', color: '#4e73df', padding: '2px 6px', borderRadius: '4px'}}>YENİLEME</span>}
                      <div style={{display: 'flex', gap: '5px', marginTop: '10px'}}>
                          <button onClick={() => handleRequestAction(req.id, 'approved')} style={{background: '#1cc88a', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer'}}>Onayla</button>
                          <button onClick={() => handleRequestAction(req.id, 'rejected')} style={{background: '#e74a3b', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer'}}>Reddet</button>
                          {req.clinic_phone && (
                              <a href={`https://wa.me/${req.clinic_phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" style={{background: '#25D366', color: 'white', padding: '5px 10px', borderRadius: '5px', textDecoration: 'none', display: 'flex', alignItems: 'center'}}>
                                  WP
                              </a>
                          )}
                      </div>
                  </div>
                  );
              })}
          </div>
      )}

      {/* Klinik Sahibi Abonelik Uyarısı */}
      {showRenewalAlert && user.role === 'admin' && (
          <div style={{position: 'fixed', bottom: '20px', right: '20px', background: '#fff3cd', padding: '20px', borderRadius: '10px', boxShadow: '0 5px 20px rgba(0,0,0,0.2)', zIndex: 9998, maxWidth: '350px', border: '1px solid #ffeeba', borderLeft: '5px solid #f6c23e'}}>
              <h4 style={{marginTop: 0, color: '#856404'}}>⚠️ Abonelik Uyarısı</h4>
              <p style={{color: '#856404', marginBottom: '15px'}}>
                  Paketinizin kullanım süresi dolmak üzere (Bitiş: {new Date(settings.subscription_end).toLocaleDateString()}). 
                  Hizmet kesintisi yaşamamak için lütfen paketinizi yenileyin.
              </p>
              <button onClick={handleRenewalRequest} style={{background: '#f6c23e', color: '#333', border: 'none', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', width: '100%'}}>Paketimi Yeniledim / Bildir</button>
          </div>
      )}

      <DockSidebar user={{...user, settings}} onTabOpen={handleOpenTab} onLogout={onLogout} isExpanded={isSidebarExpanded} onHover={() => setIsSidebarExpanded(true)} onLeave={() => setIsSidebarExpanded(false)} />
      
      <Topbar 
        openTabs={openTabs} 
        activeTabId={activeTabId}
        onTabClick={setActiveTabId}
        onTabClose={handleCloseTab}
        onReorderTabs={handleReorderTabs}
        onCloseOthers={handleCloseOthers}
        onCloseAll={handleCloseAll}
        isFullScreen={isFullScreen}
        onToggleFullScreen={() => setIsFullScreen(!isFullScreen)}
        notifications={notifications}
        onNotificationClick={handleNotificationClick}
        onClearNotifications={handleClearNotifications}
      />

      <main className="content-window-area">
        {openTabs.map(tab => {
          const Component = componentMap[tab.id];
          const props = {
            user: user,
            onUpdateUser: onUpdateUser,
            openTab: handleOpenTab,
            currentUser: user, // for Users.js
            tabData: tab.data // Sekmeye özel veriyi gönder
          };
          return (
            <div key={tab.id} className={`tab-panel ${tab.id === activeTabId ? 'active' : ''}`}>
              {Component ? <Component {...props} /> : <div>Component not found for {tab.id}</div>}
            </div>
          );
        })}
      </main>
    </div>
  );
}

export default Dashboard;